import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search as SearchIcon, ExternalLink, Loader2 } from 'lucide-react';

const Search = () => {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleLoading, setArticleLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setSearched(true);
    setSelectedArticle(null);

    try {
      const response = await axios.get(`${API}/wikipedia/search`, {
        params: { query, lang: i18n.language }
      });
      setResults(response.data.results);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const viewArticle = async (pageid) => {
    setArticleLoading(true);
    try {
      const response = await axios.get(`${API}/wikipedia/article/${pageid}`, {
        params: { lang: i18n.language }
      });
      setSelectedArticle(response.data);
    } catch (error) {
      console.error('Article error:', error);
    } finally {
      setArticleLoading(false);
    }
  };

  return (
    <div className="pt-16 min-h-screen bg-[#050505]" data-testid="search-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8 text-center">
          <span className="text-sm uppercase tracking-[0.2em] font-semibold text-zinc-500">
            {t('nav.search')}
          </span>
          <h1 className="font-outfit text-4xl md:text-5xl font-black text-white mt-2">
            {t('search.title')} <span className="text-red-500">Wikipedia</span>
          </h1>
          <p className="text-zinc-400 mt-4 max-w-2xl mx-auto">
            Search millions of articles with images and related topics
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-12">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('search.placeholder')}
                className="pl-12 py-6 bg-[#121212] border-zinc-800 focus:border-red-500 text-white text-lg"
                data-testid="search-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 rounded-sm"
              data-testid="search-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t('search.searchBtn')
              )}
            </Button>
          </div>
        </form>

        {/* Results */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Results List */}
          <div className={`${selectedArticle ? 'lg:col-span-1' : 'lg:col-span-3'}`}>
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-12 h-12 text-red-500 animate-spin" />
              </div>
            ) : searched && results.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-zinc-500 text-lg">{t('search.noResults')}</p>
              </div>
            ) : (
              <div className={`grid ${selectedArticle ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'} gap-4`}>
                {results.map((result, idx) => (
                  <div
                    key={result.pageid}
                    onClick={() => viewArticle(result.pageid)}
                    className={`card-hover bg-[#121212] rounded-sm overflow-hidden cursor-pointer animate-fadeIn ${
                      selectedArticle?.pageid === result.pageid ? 'border-red-500' : ''
                    }`}
                    data-testid={`search-result-${idx}`}
                  >
                    {result.thumbnail && (
                      <div className="h-32 overflow-hidden">
                        <img
                          src={result.thumbnail}
                          alt={result.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold text-white mb-2 line-clamp-2">{result.title}</h3>
                      <p className="text-sm text-zinc-400 line-clamp-3">{result.extract}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Article Detail */}
          {selectedArticle && (
            <div className="lg:col-span-2 card-hover bg-[#121212] rounded-sm p-6 animate-fadeIn">
              {articleLoading ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="w-12 h-12 text-red-500 animate-spin" />
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-start mb-6">
                    <h2 className="font-outfit text-2xl font-bold text-white">{selectedArticle.title}</h2>
                    <a
                      href={selectedArticle.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-red-500 hover:text-red-400"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t('search.readMore')}
                    </a>
                  </div>

                  {selectedArticle.thumbnail && (
                    <img
                      src={selectedArticle.thumbnail}
                      alt={selectedArticle.title}
                      className="w-full max-h-80 object-cover rounded-sm mb-6"
                    />
                  )}

                  <div className="prose prose-invert max-w-none">
                    <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">
                      {selectedArticle.extract}
                    </p>
                  </div>

                  {selectedArticle.categories && selectedArticle.categories.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-zinc-800">
                      <h4 className="text-sm font-semibold text-zinc-400 mb-3">Categories</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedArticle.categories.slice(0, 8).map((cat, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 text-xs bg-zinc-800 text-zinc-300 rounded-sm"
                          >
                            {cat}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedArticle.links && selectedArticle.links.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-zinc-800">
                      <h4 className="text-sm font-semibold text-zinc-400 mb-3">{t('search.relatedTopics')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedArticle.links.slice(0, 10).map((link, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              setQuery(link);
                              handleSearch({ preventDefault: () => {} });
                            }}
                            className="px-3 py-1 text-xs bg-red-500/10 text-red-500 rounded-sm hover:bg-red-500/20 transition-colors"
                          >
                            {link}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Search;
